<template>
  <ContainerWrapper>
    <OffersList
      v-if="cptdDisplayCarEquityOffers"
      title="Com carro em garantia"
      description="Para saber se você tem ofertas pré-aprovadas, faça uma simulação."
      :offer-list="cptdCarEquityOffers"
    >
      <template v-if="displayCarEquityFakeOffers">
        <ProductEmptyStateOfferCard
          v-for="offer in cptdEmptyStateOffers"
          :key="offer.amount"
          :amount="offer.amount"
          :duration="offer.duration"
          :interest-rate="offer.interestRate"
          product="AUTO_EQUITY"
          form-path="/forms/emprestimo/auto/simular"
          :form-query="{
            vehicle_type: 'CAR',
            simulation_source: `fake_offer_auto_${offer.amount}`,
          }"
        />
      </template>
    </OffersList>
    <OffersList
      v-if="cptdDisplayMotorcycleEquityOffers"
      title="Com moto em garantia"
      description="Para saber se você tem ofertas pré-aprovadas, faça uma simulação."
      :offer-list="cptdMotorcycleEquityOffers"
    >
      <template v-if="displayMotorcycleEquityFakeOffers">
        <ProductEmptyStateOfferCard
          v-for="offer in cptdEmptyStateOffers"
          :key="offer.amount"
          :amount="offer.amount"
          :duration="offer.duration"
          :interest-rate="offer.interestRate"
          product="MOTORCYCLE_EQUITY"
          form-path="/forms/emprestimo/auto/simular"
          :form-query="{
            vehicle_type: 'MOTORCYCLE',
            simulation_source: `fake_offer_auto_${offer.amount}`,
          }"
        />
      </template>
    </OffersList>
    <AutoEquityColdOfferNoResultFeedbackDialogContainer
      v-if="displayAutoEquityColdOfferNoResultFeedbackDialog"
      :display="displayAutoEquityColdOfferNoResultFeedbackDialog"
      @close="displayAutoEquityColdOfferNoResultFeedbackDialog = false"
    />
    <AutoEquityOfferNoResultFeedbackDialogContainer
      v-if="displayAutoEquityNoResultFeedbackDialog"
      :display="displayAutoEquityNoResultFeedbackDialog"
      :redirect="false"
      @close="displayAutoEquityNoResultFeedbackDialog = false"
    />
  </ContainerWrapper>
</template>
<script>
import { mapGetters } from 'vuex'
import ContainerWrapper from '@/components/ui/newComponents/ContainerWrapper.vue'
import OffersList from '@/components/appForm/loanResults/OffersList.vue'
import { isDateLowerThan, localDate } from '@/helpers/date'
import { defineAsyncComponent } from 'vue'
import { AutoType } from '~/data/common'
const ProductEmptyStateOfferCard = defineAsyncComponent(
  () => import('@/components/loans/ProductEmptyStateOfferCard.vue'),
)
const AutoEquityColdOfferNoResultFeedbackDialogContainer = defineAsyncComponent(
  () =>
    import(
      '@/containers/forms/loans/auto/cold-offer/AutoEquityColdOfferNoResultFeedbackDialogContainer.vue'
    ),
)
const AutoEquityOfferNoResultFeedbackDialogContainer = defineAsyncComponent(
  () =>
    import(
      '@/containers/forms/loans/auto/AutoEquityNoResultFeedbackDialogContainer.vue'
    ),
)

export default {
  name: 'AutoEquityOffersListContainer',
  components: {
    AutoEquityOfferNoResultFeedbackDialogContainer,
    ProductEmptyStateOfferCard,
    ContainerWrapper,
    AutoEquityColdOfferNoResultFeedbackDialogContainer,
    OffersList,
  },
  data() {
    return {
      displayAutoEquityColdOfferNoResultFeedbackDialog: false,
      displayAutoEquityNoResultFeedbackDialog: false,
      displayCarEquityFakeOffers: false,
      displayMotorcycleEquityFakeOffers: false,
    }
  },
  computed: {
    ...mapGetters({
      gtAutoColdOffers:
        'products/loans/auto-equity/cold-offer/gtAutoColdOffers',
      gtAllValidatingAutoOffers:
        'products/loans/auto-equity/auto-equity/gtAllValidatingAutoOffers',
      gtAllProposedAutoOffers:
        'products/loans/auto-equity/auto-equity/gtAllProposedAutoOffers',
      gtAutoEquityLatestColdOfferRequest:
        'products/loans/auto-equity/cold-offer/gtAutoEquityLatestColdOfferRequest',
    }),
    cptdAllAutoEquityOffers() {
      return [...this.cptdAutoEquityOffers, ...this.cptdAutoEquityColdOffers]
    },
    cptdAutoEquityColdOffers() {
      return this.gtAutoColdOffers
    },
    cptdAutoEquityOffers() {
      return [
        ...this.gtAllProposedAutoOffers,
        ...this.gtAllValidatingAutoOffers,
      ]
    },
    cptdCarEquityOffers() {
      return this.cptdAllAutoEquityOffers.filter(({ auto_type }) =>
        [AutoType.CAR, AutoType.TRUCK].includes(auto_type),
      )
    },
    cptdMotorcycleEquityOffers() {
      return this.cptdAllAutoEquityOffers.filter(({ auto_type }) =>
        [AutoType.MOTORCYCLE].includes(auto_type),
      )
    },
    cptdDisplayCarEquityOffers() {
      return (
        this.cptdCarEquityOffers.length > 0 || this.displayCarEquityFakeOffers
      )
    },
    cptdDisplayMotorcycleEquityOffers() {
      return (
        this.cptdMotorcycleEquityOffers.length > 0 ||
        this.displayMotorcycleEquityFakeOffers
      )
    },
    cptdEmptyStateOffers() {
      return [
        {
          amount: 50000,
          duration: 60,
          interestRate: 1.49,
        },
        {
          amount: 17000,
          duration: 60,
          interestRate: 1.49,
        },
        {
          amount: 10000,
          duration: 60,
          interestRate: 1.49,
        },
        {
          amount: 5000,
          duration: 60,
          interestRate: 1.49,
        },
      ]
    },
  },
  mounted() {
    const hasSimulation = Object.keys(
      this.gtAutoEquityLatestColdOfferRequest || {},
    ).length
    const simulatedMoreThanAMonthBefore =
      this.gtAutoEquityLatestColdOfferRequest?.created &&
      isDateLowerThan(
        localDate(this.gtAutoEquityLatestColdOfferRequest?.created),
        new Date(Date.now() - 24 * 60 * 60 * 1000 * 30),
      )
    this.displayCarEquityFakeOffers =
      this.cptdCarEquityOffers.length === 0 &&
      (!hasSimulation || simulatedMoreThanAMonthBefore)
    this.displayMotorcycleEquityFakeOffers =
      this.cptdMotorcycleEquityOffers.length === 0 &&
      (!hasSimulation || simulatedMoreThanAMonthBefore)
    this.checkLastSimulationResult()
  },
  methods: {
    checkLastSimulationResult() {
      const { last_product_simulation } = this.$route?.query || {}
      this.displayAutoEquityColdOfferNoResultFeedbackDialog =
        last_product_simulation === 'auto' &&
        this.cptdAutoEquityColdOffers.length === 0
      this.displayAutoEquityNoResultFeedbackDialog =
        last_product_simulation === 'auto-equity' &&
        this.cptdAutoEquityOffers.length === 0
    },
  },
}
</script>
<style lang="scss"></style>
